import { useState } from 'react';
import { formatCents } from '../api';
import { messageText, totalLabel } from '../i18n';
import type { CartView } from '../types';

const TEST_BUYER = {
  email: 'ucp-demo@paulmann-test.de',
  first_name: 'Testa',
  last_name: 'Bestellerin',
  phone: '+49 5151 0000',
};

const TEST_ADDRESS = {
  street: 'Keniastraße',
  house_number: '24',
  zip: '31789',
  city: 'Hameln',
  country: 'DE' as const,
};

interface Props {
  cart: CartView;
  busy: boolean;
  onQuantity: (productId: string, quantity: number) => void;
  onRemove: (productId: string) => void;
  onBuyer: (payload: { buyer: typeof TEST_BUYER; shipping_address: typeof TEST_ADDRESS }) => void;
  onConfirm: () => void;
  onPay: () => void;
  onCancel: () => void;
}

export function Cart({ cart, busy, onQuantity, onRemove, onBuyer, onConfirm, onPay, onCancel }: Props) {
  const [buyer, setBuyer] = useState(TEST_BUYER);
  const [address, setAddress] = useState(TEST_ADDRESS);

  const hasItems = cart.items.length > 0;
  const grandTotal = cart.totals.find((total) => total.type === 'total');

  if (cart.orderNumber) {
    return (
      <section className="panel" aria-labelledby="cart-heading">
        <h2 id="cart-heading">Bestellung bestätigt</h2>
        <div className="notice ok">
          <p style={{ margin: '0 0 0.5rem' }}>Der Shop hat die Testbestellung bestätigt.</p>
          <p className="order-number">{cart.orderNumber}</p>
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem' }}>
            UCP-Sitzung <code>{cart.checkoutId}</code>
            <br />
            Zahlungsstatus <code>{cart.paymentStatus ?? '–'}</code>
          </p>
        </div>
        <ul className="totals">
          {cart.totals.map((total) => (
            <li key={total.type} className={total.type === 'total' ? 'grand' : undefined}>
              <span>{totalLabel(total.type, total.label)}</span>
              <span>{formatCents(total.amountCents, cart.currency)}</span>
            </li>
          ))}
        </ul>
      </section>
    );
  }

  return (
    <section className="panel" aria-labelledby="cart-heading">
      <h2 id="cart-heading">Warenkorb</h2>

      {!hasItems && <p className="empty">Noch nichts im Warenkorb. Suche links nach einem Produkt.</p>}

      {hasItems && (
        <>
          <ul className="cart-lines">
            {cart.items.map((item) => (
              <li key={item.productId}>
                <div className="line-top">
                  <span className="line-title">{item.title}</span>
                  <span>{formatCents(item.totalPriceCents, cart.currency)}</span>
                </div>
                <span className="sku" style={{ fontSize: '0.75rem', color: 'var(--ink-soft)' }}>
                  Art.-Nr. {item.productId} · {formatCents(item.unitPriceCents, cart.currency)} je Stück
                </span>
                <div className="qty">
                  <button
                    type="button"
                    className="step"
                    aria-label={`Menge für ${item.title} verringern`}
                    disabled={busy || cart.locked || item.quantity <= 1}
                    onClick={() => onQuantity(item.productId, item.quantity - 1)}
                  >
                    −
                  </button>
                  <output aria-label={`Menge ${item.quantity}`}>{item.quantity}</output>
                  <button
                    type="button"
                    className="step"
                    aria-label={`Menge für ${item.title} erhöhen`}
                    disabled={busy || cart.locked}
                    onClick={() => onQuantity(item.productId, item.quantity + 1)}
                  >
                    +
                  </button>
                  <button
                    type="button"
                    className="btn subtle"
                    style={{ marginLeft: 'auto', padding: '0.2rem 0.5rem', fontSize: '0.8125rem' }}
                    disabled={busy || cart.locked}
                    onClick={() => onRemove(item.productId)}
                  >
                    Entfernen
                  </button>
                </div>
              </li>
            ))}
          </ul>

          <ul className="totals">
            {cart.totals.map((total) => (
              <li key={total.type} className={total.type === 'total' ? 'grand' : undefined}>
                <span>{totalLabel(total.type, total.label)}</span>
                <span>{formatCents(total.amountCents, cart.currency)}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {cart.messages.map((message, index) => (
        <p key={index} className={`notice ${message.type === 'error' ? 'error' : 'info'}`}>
          {messageText(message.code, message.content)}
        </p>
      ))}

      {hasItems && cart.status !== 'ready_for_complete' && !cart.locked && (
        <form
          className="buyer"
          aria-label="Käufer- und Lieferdaten"
          onSubmit={(event) => {
            event.preventDefault();
            onBuyer({ buyer, shipping_address: address });
          }}
        >
          <p className="notice info" style={{ margin: 0 }}>
            Synthetische Testdaten sind vorbelegt. Lieferung nur nach Deutschland.
          </p>
          <label>
            E-Mail (Testadresse)
            <input
              type="email"
              required
              value={buyer.email}
              onChange={(event) => setBuyer({ ...buyer, email: event.target.value })}
            />
          </label>
          <div className="field-row">
            <label>
              Vorname
              <input
                required
                value={buyer.first_name}
                onChange={(event) => setBuyer({ ...buyer, first_name: event.target.value })}
              />
            </label>
            <label>
              Nachname
              <input
                required
                value={buyer.last_name}
                onChange={(event) => setBuyer({ ...buyer, last_name: event.target.value })}
              />
            </label>
          </div>
          <div className="field-row">
            <label>
              Straße
              <input
                required
                value={address.street}
                onChange={(event) => setAddress({ ...address, street: event.target.value })}
              />
            </label>
            <label>
              Hausnummer
              <input
                required
                value={address.house_number}
                onChange={(event) => setAddress({ ...address, house_number: event.target.value })}
              />
            </label>
          </div>
          <div className="field-row">
            <label>
              PLZ
              <input
                required
                inputMode="numeric"
                value={address.zip}
                onChange={(event) => setAddress({ ...address, zip: event.target.value })}
              />
            </label>
            <label>
              Ort
              <input
                required
                value={address.city}
                onChange={(event) => setAddress({ ...address, city: event.target.value })}
              />
            </label>
          </div>
          <button className="btn block" type="submit" disabled={busy}>
            Lieferdaten übernehmen
          </button>
        </form>
      )}

      {cart.state === 'payment_pending' && (
        <div className="notice warn">
          <p style={{ margin: 0 }}>
            Die Testzahlung läuft. Der Warenkorb ist gesperrt, bis der Shop den Status bestätigt.
            Eine Rückleitung allein bedeutet noch keinen Zahlungserfolg.
          </p>
          {cart.paymentRedirectUrl && (
            <p style={{ margin: '0.5rem 0 0' }}>
              <a href={cart.paymentRedirectUrl}>Zahlungsseite erneut öffnen</a>
            </p>
          )}
        </div>
      )}

      {cart.state === 'payment_confirmed_order_pending' && (
        <p className="notice warn">
          Zahlung bestätigt, die Bestellung wird noch geprüft. Der Wiederanlauf versucht
          ausschließlich die Bestellerstellung erneut — es entsteht keine zweite Zahlung.
        </p>
      )}

      {cart.state === 'payment_failed' && (
        <p className="notice error">
          Die Testzahlung wurde nicht abgeschlossen. Es entstand keine Bestellung. Bestätige die
          Übersicht erneut, um einen neuen Versuch zu starten.
        </p>
      )}

      {cart.state === 'escalation' && (
        <p className="notice error">
          Die Sitzung erfordert eine manuelle Klärung. Bitte an die Betriebsverantwortlichen wenden.
        </p>
      )}

      {hasItems && cart.status === 'ready_for_complete' && !cart.locked && (
        <div style={{ padding: '0.75rem 1rem', display: 'grid', gap: '0.6rem' }}>
          {!cart.confirmationValid ? (
            <>
              <p className="notice info" style={{ margin: 0 }}>
                Bitte Positionen, Gesamtpreis und Lieferadresse prüfen. Mit der Bestätigung wird
                genau diese Fassung verbindlich; jede Änderung danach macht sie ungültig.
              </p>
              <button className="btn block" type="button" disabled={busy} onClick={onConfirm}>
                Bestellübersicht bestätigen
                {grandTotal ? ` · ${formatCents(grandTotal.amountCents, cart.currency)}` : ''}
              </button>
            </>
          ) : (
            <>
              <p className="notice ok" style={{ margin: 0 }}>
                Bestätigt. Die Zahlung ist eine Testzahlung ohne realen Geldfluss.
              </p>
              <button className="btn block large" type="button" disabled={busy} onClick={onPay}>
                Testbestellung auslösen
              </button>
            </>
          )}
          <button className="btn subtle block" type="button" disabled={busy} onClick={onCancel}>
            Sitzung abbrechen
          </button>
        </div>
      )}
    </section>
  );
}
